import { getMediaType, insertCloudinaryTransformation } from '.';

const CLOUDINARY_AUTO_OFFSET = 'so_auto';

// A keyframe every second keeps each scrubber seek to a few small decoded frames;
// uploads are up to 1080p with keyframes ~10 seconds apart.
const SCRUB_VIDEO_TRANSFORMATION =
  'c_limit,w_480,h_480,q_auto:eco,ac_none,ki_1';

export const THUMBNAIL_PREVIEW_WIDTH = 400;

export const THUMBNAIL_FILMSTRIP_WIDTH = 160;

export const THUMBNAIL_FILMSTRIP_FRAME_COUNT = 6;

/** One frame at 30fps: the finest step the scrubber can meaningfully offer. */
export const THUMBNAIL_SCRUB_STEP_SECONDS = 1 / 30;

const roundOffsetSeconds = (offsetSeconds: number): number =>
  Math.max(0, Math.round(offsetSeconds * 100) / 100);

export const supportsFrameSelection = (loopUrl?: string): boolean =>
  !!loopUrl &&
  loopUrl.includes('cloudinary.com') &&
  getMediaType(loopUrl) === 'video';

// Mirrors the api's transformation, so the studio previews the file production
// will serve. No offset means `so_auto`, Cloudinary's own pick.
export const getKlydoStillFrameUrl = ({
  loopUrl,
  offsetSeconds,
  width = THUMBNAIL_PREVIEW_WIDTH,
}: {
  loopUrl: string;
  offsetSeconds?: number | null;
  width?: number;
}): string => {
  const startOffset =
    offsetSeconds === undefined ||
    offsetSeconds === null ||
    !Number.isFinite(offsetSeconds)
      ? CLOUDINARY_AUTO_OFFSET
      : `so_${roundOffsetSeconds(offsetSeconds)}`;

  return insertCloudinaryTransformation(
    loopUrl,
    `${startOffset},w_${width},c_scale,q_auto,f_png`,
  );
};

export const getKlydoScrubVideoUrl = (loopUrl: string): string =>
  insertCloudinaryTransformation(loopUrl, SCRUB_VIDEO_TRANSFORMATION);

// Segment midpoints, not edges: a klydo loop is seamless, so its first and last
// frames are the same picture.
export const getFilmstripOffsets = (
  durationInSeconds?: number,
  frameCount: number = THUMBNAIL_FILMSTRIP_FRAME_COUNT,
): number[] => {
  if (
    !durationInSeconds ||
    !Number.isFinite(durationInSeconds) ||
    durationInSeconds <= 0 ||
    frameCount <= 0
  ) {
    return [];
  }

  return Array.from({ length: frameCount }, (_, index) =>
    roundOffsetSeconds(((index + 0.5) / frameCount) * durationInSeconds),
  );
};

export const clampFrameOffset = (
  offsetSeconds: number,
  durationInSeconds?: number,
): number => {
  const safeOffset = Number.isFinite(offsetSeconds) ? offsetSeconds : 0;
  if (
    !durationInSeconds ||
    !Number.isFinite(durationInSeconds) ||
    durationInSeconds <= 0
  ) {
    return roundOffsetSeconds(safeOffset);
  }
  return roundOffsetSeconds(Math.min(safeOffset, durationInSeconds));
};

/** `2.44` -> `0:02.4` — the scrubber reads in tenths, loops are seconds long. */
export const formatFrameOffset = (offsetSeconds: number): string => {
  const safeOffset = Math.max(
    0,
    Number.isFinite(offsetSeconds) ? offsetSeconds : 0,
  );
  const minutes = Math.floor(safeOffset / 60);
  const seconds = Math.floor(safeOffset % 60);
  const tenths = Math.floor((safeOffset * 10) % 10);

  return `${minutes}:${String(seconds).padStart(2, '0')}.${tenths}`;
};
